import { Dialog } from "@dash/dialog";
import { Button, TextField } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { useProfiles } from "@megarax/features";
import { useTheme } from "@emotion/react";
import React, { useContext, useState } from "react";
import { MdOutlineMailOutline } from "react-icons/md";
import { useQueryClient } from "react-query";

import { IamAuthContext } from "@megaron/auth-react";
import { useClientManager } from "@megaron/react-clients";
import { TicketDetailDto } from "@megaron/support-contracts";

type Props = {
  ticket: TicketDetailDto;
  onClose: () => void;
};

export const SupportTicketReplyComposer: React.FC<Props> = ({ ticket, onClose }) => {
  const theme = useTheme();
  const { isMobile } = useDeviceType();
  const queryClient = useQueryClient();
  const support = useClientManager("support");
  const auth = useContext(IamAuthContext);
  const { profile } = useProfiles();
  const [replyBody, setReplyBody] = useState("");
  const userEmail = auth.iamUser?.userType === "megarax" ? auth.iamUser.email : undefined;
  const senderProfile = profile(userEmail ?? null);

  const sendMutation = support.emailSend().useMutation({
    onSuccess: () => {
      setReplyBody("");
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
      onClose();
    },
  });

  const isSending = "isLoading" in sendMutation ? sendMutation.isLoading : false;

  const handleSend = () => {
    const trimmed = replyBody.trim();
    if (!trimmed) return;
    sendMutation.mutate({
      to: ticket.reporterEmail,
      subject: ticket.subject.startsWith("Re:") ? ticket.subject : `Re: ${ticket.subject}`,
      body: trimmed,
      threadId: ticket.threadId ?? undefined,
      senderFirstName: senderProfile?.firstName ?? null,
      senderLastName: senderProfile?.lastName ?? null,
    });
  };

  return (
    <Dialog
      header="Odpowiedz klientowi"
      onClose={onClose}
      placement={isMobile ? "top" : "center"}
      css={{ width: "600px" }}
      footer={
        <div css={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0.5rem", width: "100%" }}>
          <Button variant="text" onClick={onClose} isDisabled={isSending}>
            Anuluj
          </Button>
          <Button
            icon={<MdOutlineMailOutline />}
            onClick={handleSend}
            isDisabled={!replyBody.trim() || isSending}
            isLoading={isSending}
            spinnerColor="white"
          >
            Wyślij
          </Button>
        </div>
      }
    >
      <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div css={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8125rem", color: theme.colors.secondaryText }}>
          <MdOutlineMailOutline size={16} />
          <span>
            Odpowiedź od: <strong>{ticket.sourceMailbox ?? "skrzynka support"}</strong> → {ticket.reporterEmail}
          </span>
        </div>
        <TextField
          value={replyBody}
          onChange={setReplyBody}
          label="Treść odpowiedzi"
          multiline
          autoFocus
          isDisabled={isSending}
          css={{ textarea: { minHeight: "10rem", resize: "vertical", fontFamily: "inherit" } }}
          error={sendMutation.isError ? "Nie udało się wysłać wiadomości. Sprawdź ustawienia SMTP." : null}
        />
      </div>
    </Dialog>
  );
};
